import { useNavigate } from 'react-router-dom'
import { useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { formatDPI } from '../../../../utils/masks/formatDPI'
import { Button } from '../../../../components/button'
import { PageContainer } from '../../../../components/page-container/page-container'
import { paths } from '../../../../router/paths'
import { Input } from '../../../../components/input'
import { userService } from '../../../user/service/user-service'
import { showToast } from '../../../../components/sonner'
import { setUserInformation } from '../../../../store/user-onboarding'
import { onboardingText, onboardingStyles } from '../styles/onboarding-styles'

export const CreateUser = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Estados dos campos
  // dpiMasked: valor exibido; dpiRaw: somente dígitos
  const [dpiMasked, setDpiMasked] = useState('')
  const [dpiRaw, setDpiRaw] = useState('')
  const [agencyCode, setAgencyCode] = useState('')
  const [employeeNumber, setEmployeeNumber] = useState('')

  // Estados de erro
  const [dpiError, setDpiError] = useState<string | undefined>()
  const [agencyError, setAgencyError] = useState<string | undefined>()
  const [employeeError, setEmployeeError] = useState<string | undefined>()

  // Touched flags
  const [dpiTouched, setDpiTouched] = useState(false)
  const [agencyTouched, setAgencyTouched] = useState(false)
  const [employeeTouched, setEmployeeTouched] = useState(false)

  const [loading, setLoading] = useState(false)

  // Handlers com máscara e validação básica
  const handleDpiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 13)
    setDpiRaw(raw)
    const masked = formatDPI(raw)
    setDpiMasked(masked || '')
    if (raw.length === 13) {
      setDpiError(undefined)
    } else {
      setDpiError('DPI debe tener 13 dígitos')
    }
  }

  const handleAgencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyDigits = e.target.value
    setAgencyCode(onlyDigits)
    if (!onlyDigits) {
      setAgencyError('Ingrese el código de agencia')
    } else {
      setAgencyError(undefined)
    }
  }

  const handleEmployeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyDigits = e.target.value.replace(/\D/g, '')
    setEmployeeNumber(onlyDigits)
    if (!onlyDigits) {
      setEmployeeError('Ingrese número de empleado')
    } else {
      setEmployeeError(undefined)
    }
  }

  // Validação geral do formulário
  const isFormValid = useMemo(() => {
    return (
      dpiRaw.length === 13 &&
      !!agencyCode &&
      !!employeeNumber &&
      !dpiError &&
      !agencyError &&
      !employeeError
    )
  }, [dpiRaw, agencyCode, employeeNumber, dpiError, agencyError, employeeError])

  const handleSubmit = async () => {
    if (!isFormValid) return
    try {
      setLoading(true)
      const response = await userService.findUserInformation({
        dpi: dpiRaw,
        agencyCode: agencyCode,
        employeeNumber: employeeNumber,
      })

      if (response
        && response.userInformationId
        && response.dpi !== undefined
        && response.employeeNumber
        && response.agencyId !== undefined
        && response.isFirstAccess
      ) {
        const { dpi, employeeNumber: emp, agencyId, isFirstAccess, userInformationId } = response
        dispatch(
          setUserInformation({
            dpi,
            employeeNumber: emp,
            agencyId,
            isFirstAccess,
            userInformationId,
          })
        )
        navigate(paths.user.createPin)
      } else if (response && response.isFirstAccess === false) {
        showToast('El usuario ya tiene acceso', 'error')
      } else {
        showToast('No se pudo validar la información del usuario', 'error')
      }
    } catch (e) {
      showToast('Error al consultar la información. Intente de nuevo', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageContainer type="unauthenticated">
      <div className={onboardingStyles.createUserOuter}>
        <div className={onboardingStyles.createUserHeader}>
          <p className={onboardingStyles.createUserTitle}>{onboardingText.createUser.title}</p>
          <div className={onboardingStyles.createUserInputsGap}>
            <Input
              onChange={handleDpiChange}
              onBlur={() => setDpiTouched(true)}
              value={dpiMasked}
              placeholder="DPI"
              name="dpi"
              error={dpiTouched ? dpiError : undefined}
              maxLength={15} // inclui os hifens
              type="text"
            />
            <Input
              onChange={handleAgencyChange}
              onBlur={() => setAgencyTouched(true)}
              value={agencyCode}
              placeholder="Código de la agencia"
              name="agencyCode"
              error={agencyTouched ? agencyError : undefined}
              type="text"
            />
            <Input
              onChange={handleEmployeeChange}
              onBlur={() => setEmployeeTouched(true)}
              value={employeeNumber}
              placeholder="Número de empleado"
              name="employeeNumber"
              error={employeeTouched ? employeeError : undefined}
              type="text"
            />
          </div>
        </div>
        <div className="flex w-full flex-col items-center pt-2 sm:pt-0">
          <Button
            className={onboardingStyles.createUserButton}
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
          >
            {loading ? 'Cargando...' : onboardingText.createUser.submit}
          </Button>
          {!isFormValid && (
            <p className={onboardingStyles.createUserHint}>{onboardingText.createUser.formHint}</p>
          )}
        </div>
        <div className="flex w-full flex-col items-center pt-4">
          <Button
            variant="outlined"
            className="h-[55px] w-full rounded-[10px] text-xl font-semibold"
            onClick={() => navigate(paths.authentication.welcome)}
          >
            Regresar
          </Button>
        </div>
        <div className={onboardingStyles.createUserSwitchBlock}>
          <p className="text-center text-xs font-normal text-gray-700 sm:text-sm">
            {onboardingText.createUser.switchToLogin}{' '}
            <span
              onClick={() => navigate(paths.authentication.login)}
              className="cursor-pointer font-normal text-[#2D4990] hover:underline"
            >
              {onboardingText.createUser.loginCta}
            </span>
          </p>
        </div>
      </div>
    </PageContainer>
  )
}
